import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Card, Col, Row, Typography } from 'antd';
import {
  SafetyCertificateOutlined,
  FileProtectOutlined,
  AuditOutlined,
  LockOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import axios from '../../utils/axios';
import { UAM_URL } from '../../utils/externalLinks';
import { getLegalPublicBaseUrl } from '../../utils/legalPublicUrls';

const { Title, Paragraph, Text } = Typography;

const TRUST_LINKS = [
  {
    key: 'security',
    icon: <LockOutlined />,
    title: 'Безопасность платформы',
    description: 'Роли, изоляция компаний, аудит действий и защищённые интеграции.',
    to: '/product/security',
    internal: true,
  },
  {
    key: 'legal',
    icon: <FileProtectOutlined />,
    title: 'Реестр документов',
    description: 'Политика конфиденциальности, условия, cookie и согласия — актуальные версии.',
    to: '/legal',
    internal: true,
  },
  {
    key: 'privacy-center',
    icon: <AuditOutlined />,
    title: 'Privacy Center',
    description: 'Экспорт данных, управление согласиями, cookie-настройки и удаление аккаунта.',
    href: `${String(UAM_URL || 'https://auth.markbase.ru').replace(/\/+$/, '')}/account/privacy-center`,
    external: true,
  },
  {
    key: 'company',
    icon: <GlobalOutlined />,
    title: 'Сведения об операторе',
    description: 'Реквизиты, контакты и регистрация в реестре операторов ПДн.',
    to: '/company',
    internal: true,
  },
];

/**
 * Hub доверия (P5-B): ссылки без дублирования полных политик.
 */
export default function TrustHubWidget() {
  const [requisites, setRequisites] = useState(null);
  const [reqError, setReqError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/company-management/public-requisites-public')
      .then((resp) => {
        if (cancelled) return;
        if (resp?.data?.success) setRequisites(resp.data.data || {});
        else setReqError(true);
      })
      .catch(() => {
        if (!cancelled) setReqError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rknLine = useMemo(() => {
    if (!requisites) return null;
    const status = requisites.rkn_status || requisites.rkn_registration_status;
    const num = requisites.rkn_number;
    const date = requisites.rkn_registration_date;
    if (!num && !status) return null;
    const parts = [];
    if (status === 'registered' || status === 'Registered') parts.push('Уведомление подано');
    if (num) parts.push(`№ ${num}`);
    if (date) parts.push(`от ${date}`);
    return parts.join(', ');
  }, [requisites]);

  const privacyUrl = `${getLegalPublicBaseUrl()}/privacy`;

  return (
    <>
      <Paragraph type="secondary" style={{ marginBottom: 24, maxWidth: 720 }}>
        Markbase обрабатывает персональные данные в соответствии с 152-ФЗ. Здесь собраны ссылки на
        документы, инструменты субъекта ПДн и описание мер безопасности — без копирования полных текстов
        политик.
      </Paragraph>

      {rknLine ? (
        <Alert
          type="success"
          showIcon
          icon={<SafetyCertificateOutlined />}
          message="Оператор персональных данных"
          description={rknLine}
          style={{ marginBottom: 24 }}
        />
      ) : reqError ? (
        <Alert
          type="info"
          showIcon
          message="Реквизиты оператора"
          description="Данные временно недоступны. См. страницу «Сведения об организации»."
          style={{ marginBottom: 24 }}
        />
      ) : null}

      <Row gutter={[16, 16]}>
        {TRUST_LINKS.map((item) => (
          <Col xs={24} md={12} key={item.key}>
            <Card hoverable style={{ height: '100%' }}>
              <div style={{ fontSize: 28, color: 'var(--brand, #2563eb)', marginBottom: 12 }}>
                {item.icon}
              </div>
              <Title level={5} style={{ marginTop: 0 }}>
                {item.internal ? (
                  <Link to={item.to}>{item.title}</Link>
                ) : (
                  <a href={item.href} target="_blank" rel="noopener noreferrer">
                    {item.title}
                  </a>
                )}
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                {item.description}
              </Paragraph>
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ marginTop: 24 }} size="small">
        <Text type="secondary">
          Каноническая политика конфиденциальности:{' '}
          <a href={privacyUrl}>{privacyUrl}</a>
        </Text>
      </Card>
    </>
  );
}
